import { usePathMatchIndex } from '@features/auth/hooks';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { Box, Container } from '@mui/material';
import { motion } from 'framer-motion';
import { FC, ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import './MonitoringV2Animation.css';

// Add custom styles to prevent red border flash
const styles = `
.monitoring-v2-flow .MuiOutlinedInput-notchedOutline {
  border-color: rgba(0, 0, 0, 0.23);
}
.monitoring-v2-flow .MuiInputLabel-root {
  color: rgba(0, 0, 0, 0.6);
}
`;

// These are both route paths and translation keys
const steps = ['packages', 'terms', 'payment'];

// Animation variants for the container
const containerVariants = {
	initial: {
		opacity: 0,
		y: 20,
	},
	animate: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			when: 'beforeChildren',
			staggerChildren: 0.2,
		},
	},
	exit: {
		opacity: 0,
		y: -20,
		transition: { duration: 0.3 },
	},
};

// Animation variants for the stepper
const stepperVariants = {
	initial: {
		opacity: 0,
		y: -20,
	},
	animate: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.4 },
	},
	exit: {
		opacity: 0,
		transition: { duration: 0.2 },
	},
};

export const MonitoringV2RequestLayout: FC = (): ReactElement => {
	const { t } = useTranslation();
	usePageTitle(t('services'));
	useAppbarTitle(t('monitoring'));
	const activeStepIndex = usePathMatchIndex(steps);
	const location = useLocation();
	const navigate = useNavigate();
	const { navigateWithLanguage } = useLanguageNavigate();

	// Add a class to the body when this component mounts and remove it when unmounts
	// Also add the style to prevent red border flash
	useEffect(() => {
		document.body.classList.add('monitoring-v2-flow');

		// Add style element to prevent red border flash
		const styleElement = document.createElement('style');
		styleElement.innerHTML = styles;
		document.head.appendChild(styleElement);

		return () => {
			document.body.classList.remove('monitoring-v2-flow');
			document.head.removeChild(styleElement);
		};
	}, []);

	// Add a global navigation interceptor for monitoring-v2 flow
	useEffect(() => {
		// Monkey patch history methods to intercept navigation
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		// Monkey patch history.pushState
		window.history.pushState = function (state, title, url) {
			// Only intercept if we're already in the monitoring-v2 flow
			if (
				url &&
				typeof url === 'string' &&
				window.location.pathname.includes('monitoring-v2') &&
				url.includes('/monitoring/') &&
				!url.includes('monitoring-v2/')
			) {
				// Replace with monitoring-v2 route
				url = url.replace('/monitoring/', '/monitoring-v2/');
			}
			return originalPushState.call(this, state, title, url);
		};

		// Monkey patch history.replaceState
		window.history.replaceState = function (state, title, url) {
			// Only intercept if we're already in the monitoring-v2 flow
			if (
				url &&
				typeof url === 'string' &&
				window.location.pathname.includes('monitoring-v2') &&
				url.includes('/monitoring/') &&
				!url.includes('monitoring-v2/')
			) {
				// Replace with monitoring-v2 route
				url = url.replace('/monitoring/', '/monitoring-v2/');
			}
			return originalReplaceState.call(this, state, title, url);
		};

		// Cleanup function to restore original behavior
		return () => {
			window.history.pushState = originalPushState;
			window.history.replaceState = originalReplaceState;
		};
	}, []);

	// Handle steps navigation when the user clicks on a step in the stepper
	const handleStepClick = (index: number) => {
		const basePath = location.pathname.split('monitoring-v2')[0];
		if (index === 0) {
			navigate(`${basePath}monitoring-v2/packages`);
		} else if (index === 1) {
			navigate(`${basePath}monitoring-v2/terms`);
		} else if (index === 2) {
			navigate(`${basePath}monitoring-v2/payment`);
		}
	};

	// Function to determine direction based on path changes
	const getPathDirection = (pathname: string) => {
		// Extract the last part of the path to determine the current step
		const pathSegments = pathname.split('/').filter(Boolean);
		const currentPath = pathSegments[pathSegments.length - 1] || '';
		const currentIndex = steps.indexOf(currentPath);

		// Get the current step index, default to 0 if not found
		const currentActiveIndex = activeStepIndex !== undefined ? activeStepIndex : 0;

		// If we can't determine the direction, default to 'forward'
		if (currentIndex === -1) return 'forward';

		return currentIndex > currentActiveIndex ? 'forward' : 'backward';
	};

	// Get current direction
	const direction = getPathDirection(location.pathname);

	return (
		<motion.div
			variants={containerVariants}
			initial="initial"
			animate="animate"
			exit="exit"
			className="monitoring-v2-container"
		>
			<Box
				sx={{
					width: '100%',
					height: 'auto',
					position: 'relative',
					pt: 5,
					overflow: 'visible',
				}}
			>
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						mb: 3,
						mt: 0,
					}}
				>
					<motion.div variants={stepperVariants} style={{ zIndex: 20, position: 'relative' }}>
						{/* Simple custom stepper matching the screenshot exactly */}
						<div
							style={{
								backgroundColor: '#038877',
								height: 'auto',
								padding: '15px 0',
								borderRadius: '8px',
								width: '100%',
								maxWidth: '780px',
								margin: '0 auto',
								position: 'relative',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								zIndex: 20,
								marginBottom: '-65px', // Negative margin to overlap with card
							}}
						>
							{/* Horizontal line connecting the dots */}
							<div
								style={{
									position: 'absolute',
									top: '25px',
									left: '25%',
									right: '25%',
									height: '2px',
									backgroundColor: 'white',
								}}
							></div>

							{/* Steps */}
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									width: '80%',
									position: 'relative',
								}}
							>
								{/* Step 1 */}
								<div
									style={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}
									onClick={() => handleStepClick(0)}
								>
									<div
										style={{
											width: '16px',
											height: '16px',
											borderRadius: '50%',
											backgroundColor: 'white',
											margin: '0 auto 8px',
										}}
									></div>
									<div
										style={{
											color: 'white',
											fontSize: '14px',
											fontWeight: '500',
											textTransform: 'uppercase',
											letterSpacing: '0.5px',
										}}
									>
										{t('packages')}
									</div>
								</div>

								{/* Step 2 */}
								<div
									style={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}
									onClick={() => handleStepClick(1)}
								>
									<div
										style={{
											width: '16px',
											height: '16px',
											borderRadius: '50%',
											backgroundColor: (activeStepIndex ?? 0) >= 1 ? 'white' : '#B3DBD6',
											margin: '0 auto 8px',
										}}
									></div>
									<div
										style={{
											color: 'white',
											fontSize: '14px',
											fontWeight: '500',
											textTransform: 'uppercase',
											letterSpacing: '0.5px',
										}}
									>
										{t('terms')}
									</div>
								</div>

								{/* Step 3 */}
								<div
									style={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}
									onClick={() => handleStepClick(2)}
								>
									<div
										style={{
											width: '16px',
											height: '16px',
											borderRadius: '50%',
											backgroundColor: (activeStepIndex ?? 0) >= 2 ? 'white' : '#B3DBD6',
											margin: '0 auto 8px',
										}}
									></div>
									<div
										style={{
											color: 'white',
											fontSize: '14px',
											fontWeight: '500',
											textTransform: 'uppercase',
											letterSpacing: '0.5px',
										}}
									>
										{t('payment')}
									</div>
								</div>
							</div>
						</div>
					</motion.div>
				</Box>

				<Container
					maxWidth="lg"
					sx={{ height: 'auto', pt: 0, mt: 0, position: 'relative', zIndex: 10 }}
				>
					<motion.div
						key={location.pathname}
						initial={{
							opacity: 0,
							x: direction === 'forward' ? 100 : -100,
						}}
						animate={{
							opacity: 1,
							x: 0,
							transition: {
								type: 'spring',
								stiffness: 300,
								damping: 30,
							},
						}}
						className="monitoring-v2-form flex flex-col h-full gap-[20px] p-[20px] relative justify-between"
						style={{
							height: 'auto',
							overflow: 'visible',
							paddingTop: '30px',
							position: 'relative',
							zIndex: 10,
							boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
						}}
					>
						<Outlet />
					</motion.div>
				</Container>
			</Box>
		</motion.div>
	);
};
